import {
  ADMINISTRATIVE_AREA_LEVEL_1_KEY,
  LOCALITY_KEY,
  POSTAL_CODE_KEY,
  ROUTE_KEY,
  STREET_NUMBER_KEY,
} from '@/utils/constants'

// Explicit `| undefined` on both, not just `?`: this describes untrusted data crossing the
// Google Places boundary, where a key can be present and set to undefined.
type AddressComponent = {
  short_name?: string | undefined
  types?: readonly string[] | undefined
}

export type AutocompleteAddressFields = {
  streetNumber: string
  streetName: string
  city: string
  stateCode: string
  zipCode: string
}

const EMPTY_FIELDS: AutocompleteAddressFields = {
  streetNumber: '',
  streetName: '',
  city: '',
  stateCode: '',
  zipCode: '',
}

/**
 * Flattens a Google Places `address_components` list into the five address fields the contact
 * form holds.
 *
 * Every field is always present in the result, empty when the place carries no matching
 * component, so a caller that assigns all five clears whatever the previous suggestion left
 * behind. A place with no components at all — Places fires `place_changed` that way when the
 * input is submitted without a suggestion being picked — is not a special case here: it simply
 * matches nothing and clears everything, which is the point. Leaving a previous suggestion's
 * city/state/zip stranded beside a hand-typed street would be worse than blank, and `stateCode`
 * feeds the per-state prescribing rules downstream.
 */
export const getAddressFieldsFromPlaceResult = (
  addressComponents: readonly AddressComponent[] | null | undefined,
): AutocompleteAddressFields => {
  const fields = { ...EMPTY_FIELDS }

  for (const { short_name, types } of addressComponents ?? []) {
    const value = short_name ?? ''

    if (types?.includes(STREET_NUMBER_KEY)) {
      fields.streetNumber = value
    }

    if (types?.includes(ROUTE_KEY)) {
      fields.streetName = value
    }

    if (types?.includes(LOCALITY_KEY)) {
      fields.city = value
    }

    if (types?.includes(ADMINISTRATIVE_AREA_LEVEL_1_KEY)) {
      fields.stateCode = value
    }

    if (types?.includes(POSTAL_CODE_KEY)) {
      fields.zipCode = value
    }
  }

  return fields
}
